/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { GeometryObject } from 'geojson';
import { handleResponse, withJsonFormat } from '@opengeoweb/api';
import { authFetch } from '@opengeoweb/authentication';
import type { PlotParameter, TimeSeriesService } from '@opengeoweb/shared';
import type {
  EDRCollection,
  EDRInstance,
  EDRPositionResponse,
  EDRPositionResponseCoverageCollection,
  GeoJsonWithService,
  PointerLocation,
} from '../../components/TimeSeries/types';
import { isTimeSeriesLocation } from '../../components/TimeSeries/types';
import { getNearbyLocation } from '../../components/TimeSeries/utils';
import type { TimeseriesCollectionDetail } from '../../components/TimeSeriesSelect/utils';
import {
  createUrl,
  getBaseQueryParamArray,
  getLocationPositionPath,
} from '../edrUtils';

export interface LocationsResult {
  features: {
    id: string;
    type: 'Feature';
    geometry: GeometryObject;
    properties: {
      datetime: string;
      detail?: string;
      name?: string;
      NAME?: string;
      Name?: string;
      'parameter-name'?: string[];
    };
  }[];
}

export const getLocations = async (
  serviceUrl: string,
  collectionId: string,
): Promise<LocationsResult> => {
  const url = withJsonFormat(
    createUrl(serviceUrl, `/collections/${collectionId}/locations`),
  );
  return authFetch(url).then(handleResponse) as Promise<LocationsResult>;
};

export interface EDRServiceInformation {
  title?: string;
  description?: string;
}

export const getServiceInformation = async (
  serviceUrl: string,
): Promise<EDRServiceInformation> => {
  const url = withJsonFormat(createUrl(serviceUrl));
  return authFetch(url).then(handleResponse) as Promise<EDRServiceInformation>;
};

interface LinkObject {
  title: string;
  href: string;
  rel: string;
  type: string;
}

export const getCollections = async (
  serviceUrl: string,
): Promise<{ links: LinkObject[]; collections: EDRCollection[] }> => {
  const url = withJsonFormat(createUrl(serviceUrl, '/collections'));
  return authFetch(url).then(handleResponse) as Promise<{
    links: LinkObject[];
    collections: EDRCollection[];
  }>;
};

export const getCollection = async (
  serviceUrl: string,
  collectionId: string,
): Promise<EDRCollection> => {
  const url = withJsonFormat(
    createUrl(serviceUrl, `/collections/${collectionId}`),
  );
  return authFetch(url).then(handleResponse) as Promise<EDRCollection>;
};

export const getInstances = async (
  serviceUrl: string,
  collectionId: string,
): Promise<{ instances: EDRInstance[] }> => {
  const url = withJsonFormat(
    createUrl(serviceUrl, `/collections/${collectionId}/instances`),
  );
  return authFetch(url).then(handleResponse) as Promise<{
    instances: EDRInstance[];
  }>;
};

const getLocation = async (
  serviceUrl: string,
  collectionId: string,
  instanceId: string,
  locationId: string,
  queryParamArr: [string, string][],
): Promise<EDRPositionResponse> => {
  const path = getLocationPositionPath(collectionId, instanceId);
  const url = createUrl(
    serviceUrl,
    `${path}/locations/${locationId}`,
    queryParamArr,
  );
  return authFetch(url).then(handleResponse) as Promise<EDRPositionResponse>;
};

const getPosition = async (
  serviceUrl: string,
  collectionId: string,
  instanceId: string,
  position: PointerLocation,
  queryParamArr: [string, string][],
): Promise<EDRPositionResponse> => {
  const path = getLocationPositionPath(collectionId, instanceId);
  const { lat, lon } = position;
  const url = createUrl(serviceUrl, `${path}/position`, [
    ...queryParamArr,
    ['coords', `POINT(${lon} ${lat})`],
  ]);
  return authFetch(url).then(handleResponse) as Promise<EDRPositionResponse>;
};

const getErrorMessage = (): string => 'error-message-general';

export const fetchEdrParameter = async (
  parameter: PlotParameter,
  location: PointerLocation,
  service: TimeSeriesService,
  geojsonMap: Map<string, GeoJsonWithService>,
  intervalParam: string,
  collection: TimeseriesCollectionDetail,
  instance: EDRInstance,
): Promise<
  EDRPositionResponse | EDRPositionResponseCoverageCollection | null
> => {
  const { collectionId } = parameter;
  const queryParamArr = getBaseQueryParamArray(
    parameter,
    instance,
    collection,
    intervalParam,
  );

  if (
    isTimeSeriesLocation(location) &&
    (instance.data_queries?.locations || collection.dataQueries?.locations)
  ) {
    // The selected location contains a list of occurrences (combination of service and collection)
    const occurrence = location.occurrences?.find(
      (occ) =>
        occ.properties?.collectionId === parameter.collectionId &&
        occ.properties?.serviceId === parameter.serviceId,
    );
    if (occurrence?.id) {
      try {
        return await getLocation(
          service.url,
          collectionId,
          instance.id,
          occurrence.id,
          queryParamArr,
        );
      } catch {
        throw new Error(getErrorMessage());
      }
    }
  }

  // Try a position query
  try {
    if (instance.data_queries?.position || collection.dataQueries?.position) {
      return await getPosition(
        service.url,
        collectionId,
        instance.id,
        { lon: location.lon, lat: location.lat },
        queryParamArr,
      );
    }
  } catch {
    // As a final option look for a nearby location
    const nearbyLocation = getNearbyLocation(
      parameter,
      service,
      location,
      geojsonMap,
    );

    if (nearbyLocation) {
      try {
        return await getLocation(
          service.url,
          collectionId,
          instance.id,
          nearbyLocation.id || '',
          queryParamArr,
        );
      } catch {
        throw new Error(getErrorMessage());
      }
    }
    throw new Error(getErrorMessage());
  }

  throw new Error(getErrorMessage());
};
